/**
 * Patient API functions
 */

import { apiClient } from '../api-client';
import type { Patient, PatientListResponse, PatientStatus } from './types';

export const patientsApi = {
  /**
   * Get list of patients with filters
   */
  getPatients: async (params?: {
    status_filter?: PatientStatus;
    cohort_id?: number;
    hospital_id?: number;
    search?: string;
    skip?: number;
    limit?: number;
  }): Promise<PatientListResponse> => {
    return apiClient.get<PatientListResponse>('/patients', params);
  },

  /**
   * Get patient by ID
   */
  getPatient: async (patientId: number): Promise<Patient> => {
    return apiClient.get<Patient>(`/patients/${patientId}`);
  },

  /**
   * Create new patient
   */
  createPatient: async (patientData: Partial<Patient>): Promise<Patient> => {
    return apiClient.post<Patient>('/patients', patientData);
  },

  /**
   * Update patient
   */
  updatePatient: async (patientId: number, patientData: Partial<Patient>): Promise<Patient> => {
    return apiClient.put<Patient>(`/patients/${patientId}`, patientData);
  },

  /**
   * Delete patient
   */
  deletePatient: async (patientId: number): Promise<void> => {
    return apiClient.delete(`/patients/${patientId}`);
  },

  /**
   * Assign patient to care team
   */
  assignPatient: async (assignment: {
    patient_id: number;
    care_team_id?: number;
    doctor_id?: number;
    therapist_id?: number;
    coordinator_id?: number;
    nurse_id?: number;
    cohort_id?: number;
  }): Promise<Patient> => {
    return apiClient.post<Patient>('/patients/assign', assignment);
  },

  /**
   * Unassign patient from care team
   */
  unassignPatient: async (patientId: number): Promise<{
    message: string;
    patient_id: number;
    previous_care_team_id?: number;
  }> => {
    return apiClient.delete(`/patients/${patientId}/care-team`);
  },

  /**
   * Get patient adherence score
   */
  getPatientAdherence: async (patientId: number): Promise<{
    patient_id: number;
    overall_adherence: number;
    streak_days: number;
    status: string;
  }> => {
    return apiClient.get(`/patients/${patientId}/adherence-score`);
  },

  /**
   * Get patient dashboard summary
   */
  getPatientDashboardSummary: async (patientId: number): Promise<any> => {
    return apiClient.get(`/patients/${patientId}/dashboard-summary`);
  },

  /**
   * Get current patient's own dashboard (for patient role)
   */
  getMyDashboard: async (): Promise<any> => {
    return apiClient.get('/patients/me/dashboard');
  },
};